import { VideoLayout } from '@core/types';
import { useAppSelector } from '@online-webinar/store';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ViewEventOverlayProps {
    countdownImageUrl?: string;
    countdownVideoUrl?: string;
    isSlideViewer?: boolean;
}

export const ViewEventOverlay = ({ countdownImageUrl, countdownVideoUrl, isSlideViewer }: ViewEventOverlayProps) => {
    const { t } = useTranslation();

    const [webinarCountDownTime, setWebinarCountDownTime] = useState(0);
    const startOn = useAppSelector((state) => state.liveWebinar.startOn);
    const serverTime = useAppSelector((state) => state.liveWebinar.serverTime);
    const videoLayout = isSlideViewer ? VideoLayout.PresentationWithVideoSmall : useAppSelector((state) => state.liveWebinar.videoLayout);

    // Set countdown time
    useEffect(() => {
        if (!startOn) {
            return;
        }

        var now = serverTime ? new Date(serverTime) : new Date();
        var diff = Math.max(0, new Date(startOn).getTime() - now.getTime());
        setWebinarCountDownTime(Math.round(diff));
    }, [startOn, serverTime]);

    const [isNearStartTime, setIsNearStartTime] = useState<boolean>(false);

    const checkNearStartTime = () => {
        const minutesToStartTime = Math.floor(webinarCountDownTime / (1000 * 60));
        const minutesBeforeStartTime = 30;

        if (minutesToStartTime < minutesBeforeStartTime) {
            setIsNearStartTime(true);
        } else {
            setIsNearStartTime(false);
        }
    };

    const getDays = () => {
        return Math.floor(webinarCountDownTime / (1000 * 60 * 60 * 24)).toString();
    };

    const getDoubleDigit = (value: string) => {
        if (value.length === 1) {
            return `0${value}`;
        } else {
            return value;
        }
    };

    const getHours = () => {
        const hours = Math.floor((webinarCountDownTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)).toString();
        if (getDays() === '0') {
            return hours;
        }

        return getDoubleDigit(hours);
    };

    const getMinutes = () => {
        return getDoubleDigit(Math.floor((webinarCountDownTime % (1000 * 60 * 60)) / (1000 * 60)).toString());
    };

    const getSeconds = () => {
        return getDoubleDigit(Math.floor((webinarCountDownTime % (1000 * 60)) / 1000).toString());
    };

    useEffect(() => {
        checkNearStartTime();
    }, [getMinutes()]);

    return (
        <div
            className={`vwr-countdown relative col-start-1 row-start-1 ${isNearStartTime ? (videoLayout === VideoLayout.Classic ? '' : 'row-end-3 max-h-[calc(100dvh-4rem)]') : '-col-end-1 -row-end-1'} bg-linear-to-b from-primary/95 to-primary/75 flex justify-center z-50`}
        >
            {countdownVideoUrl && (
                <video
                    autoPlay
                    loop
                    muted
                    className={`absolute ${isNearStartTime ? 'left-1/2 w-auto h-full -translate-x-1/2' : 'inset-0 w-full h-full object-cover'}`}
                >
                    <source src={countdownVideoUrl} type="video/mp4" />
                </video>
            )}
            <div className="relative bg-[var(--c-vwr-countdown-bg)] font-light text-[var(--c-vwr-countdown-txt)] text-center p-4">
                {webinarCountDownTime === 0 && <p className="text-4xl">{t('Viewer.Message.WebinarNotStarted')}</p>}
                {webinarCountDownTime !== 0 && (
                    <>
                        <p className="text-2xl md:text-3xl xl:text-4xl mb-4">{t('Viewer.Message.WebinarPlanned')}</p>
                        <div
                            className="flex gap-2 items-start justify-center text-4xl md:text-5xl xl:text-6xl"
                            role="timer"
                            aria-atomic="true"
                            aria-live="polite"
                        >
                            {getDays() !== '0' && (
                                <>
                                    <div className="flex flex-col">
                                        <div className="flex [&>span]:w-[.6667em]">
                                            {getDays()
                                                .split('')
                                                .map((dayNumber) => (
                                                    <span>{dayNumber}</span>
                                                ))}
                                        </div>
                                        <span className="text-base">{t('Generic.Days')}</span>
                                    </div>
                                    <div className="text-075em mt-[.075em]">:</div>
                                </>
                            )}
                            <div className="flex flex-col items-center">
                                <div className="flex [&>span]:w-[.6667em]">
                                    {getHours()
                                        .split('')
                                        .map((hourNumber) => (
                                            <span>{hourNumber}</span>
                                        ))}
                                </div>
                                <span className="text-base">{t('Generic.Hour')}</span>
                            </div>
                            <div className="text-075em mt-[.075em]">:</div>
                            <div className="flex flex-col items-center">
                                <div className="flex [&>span]:w-[.6667em]">
                                    {getMinutes()
                                        .split('')
                                        .map((minuteNumber) => (
                                            <span>{minuteNumber}</span>
                                        ))}
                                </div>
                                <span className="text-base">{t('Generic.Minute')}</span>
                            </div>
                            <div className="text-075em mt-[.075em]">:</div>
                            <div className="flex flex-col items-center">
                                <div className="flex [&>span]:w-[.6667em]" {...(webinarCountDownTime > 10000 && { 'aria-hidden': 'true' })}>
                                    {getSeconds()
                                        .split('')
                                        .map((secondNumber) => (
                                            <span>{secondNumber}</span>
                                        ))}
                                </div>
                                <span className="text-base" aria-hidden="true">
                                    {t('Generic.Second')}
                                </span>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
